<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VChangeAffiliateStatus from '@modules/affiliates-approved/components/VChangeAffiliateStatus/VChangeAffiliateStatus.vue';
import VChangeDepartmentDialog from '@modules/affiliates-approved/components/VChangeDepartmentDialog/VChangeDepartmentDialog.vue';
import VChangeManagerDialog from '@modules/affiliates-approved/components/VChangeManagerDialog/VChangeManagerDialog.vue';
import VChangeNncoDialog from '@modules/affiliates-approved/components/VChangeNncoDialog/VChangeNncoDialog.vue';
import { useAffiliatesTranslate } from '@modules/affiliates-approved/composables';
import { AFFILIATES_APPROVED_PERMISSIONS } from '@modules/affiliates-approved/constants';
import {
  useDownloadAffiliatesReportMutation,
} from '@modules/affiliates-approved/mutations';
import { useAffiliatesListQuery } from '@modules/affiliates-approved/queries';
import { ADMIN_AFFILIATE_PROFILE_ROUTE_NAME } from '@modules/affiliates-approved/submodules/affiliate-profile/constants';
import { ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME } from '@modules/affiliates-approved/submodules/none-negative-carryover/constants';
import { ADMIN_AFFILIATE_TRACKERS_SETTINGS_ROUTE_NAME } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { ADMIN_POSTBACKS_LIST_ROUTE_NAME } from '@modules/postbacks/constants';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import { useUserStore } from '@modules/user/store';

import { useAffiliatesList } from './composables';

const {
  selectedIds,
  showChangeManagerDialog,
  showChangeNncoDialog,
  showChangeDepartmentDialog,
  tableFilterOptions,
  multiselectOptions,
  handleMultiselectAction,
} = useAffiliatesList();

const { translate } = useAffiliatesTranslate();

const { goToSupervision } = useUserStore();

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliatesListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadAffiliatesReportMutation(), tableState);

const columnOptions: ITableColumnsOptions = {
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  email: {
    type: ESmartFilterColumnType.CHECKBOX,
    defaultMobilePreview: true,
  },
  department_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  language_of_communication: {
    type: ESmartFilterColumnType.CHECKBOX,
    localization: {
      translationPath: 'common.languages.',
    },
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliatesListPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="translate('PAGE_HEADER')"
      :filter-props="tableFilterOptions"
      :multiselect="multiselectOptions"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @multiselect-action="handleMultiselectAction"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.AFFILIATES_SHOW)"
          icon="pi-id-card"
          :to="{ name: ADMIN_AFFILIATE_PROFILE_ROUTE_NAME, params: { id: listItem.id } }"
        />
        <VIconButton
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.SUPERVISION)"
          icon="pi-eye"
          @click="goToSupervision(listItem.id)"
        />
        <VIconButton
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.AFFILIATES_SHOW)"
          icon="pi-users"
          :to="{
            name: ADMIN_AFFILIATE_TRACKERS_SETTINGS_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <VIconButton
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.AFFILIATES_SHOW)"
          icon="pi-money-bill"
          :to="{
            name: ADMIN_AFFILIATE_NONE_NEGATIVE_CARRYOVER_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <VIconButton
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.AFFILIATES_SHOW)"
          icon="pi-desktop"
          :to="{
            name: ADMIN_POSTBACKS_LIST_ROUTE_NAME,
            params: {
              id: listItem.id,
              type: 'registration',
            },
          }"
        />
        <VChangeAffiliateStatus
          v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.CHANGE_BAN_STATUS)"
          :list-item="listItem"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATES_APPROVED_PERMISSIONS.DOWNLOAD_LIST_APPROVED)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="translate('DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>

  <VChangeManagerDialog
    v-model:visible="showChangeManagerDialog"
    :selected-affiliates="selectedIds"
    :table-state="tableState"
  />

  <VChangeNncoDialog
    v-model:visible="showChangeNncoDialog"
    :selected-affiliates="selectedIds"
    :table-state="tableState"
  />

  <VChangeDepartmentDialog
    v-model:visible="showChangeDepartmentDialog"
    :selected-ids="selectedIds"
    :filter-id="tableState?.filter_set_id"
    :page="tableState?.page"
  />

  <RouterView name="AffiliatesListPage" />
</template>
